import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, videos } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);

  const article = articles.find((a) => a.id === articleId);
  const relatedVideos = videos.slice(0, 4);
  const relatedArticles = articles.filter((a) => a.id !== articleId).slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 flex flex-col items-center justify-center min-h-[60vh]">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被删除。</p>
            <Link
              to="/"
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '黄瓜tv',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '黄瓜tv提供哪些类型的影视内容？',
        acceptedAnswer: { '@type': 'Answer', text: '黄瓜tv提供电视剧、电影、综艺、动漫、短剧、纪录片、体育、音乐等多种类型的影视内容，满足不同用户的观看需求。' },
      },
      {
        '@type': 'Question',
        name: '如何在黄瓜tv上观看高清视频？',
        acceptedAnswer: { '@type': 'Answer', text: '访问黄瓜tv官网即可在线观看高清视频，支持电脑和手机端观看。高清画质让观影体验更加出色。' },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={`黄瓜tv,${article.tags.join(',')},影视资讯`}
        canonicalUrl={`https://huanggua.tv/article/${article.id}`}
        ogImage={article.image}
        ogType="article"
        schema={{ ...articleSchema, ...faqSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: '资讯', path: '/category/zixun' },
            { label: article.title },
          ]} />

          <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                </span>
              </div>
            </header>

            <div className="overflow-hidden rounded-xl mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose max-w-none text-foreground-800 leading-relaxed text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Related Videos */}
            <section className="mt-14 pt-10 border-t border-background-200/70">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                推荐观看
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {relatedVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-video bg-background-200 mb-2">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        title={video.title}
                      />
                      <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/70 text-background-50 text-xs">
                        {video.duration}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {video.title}
                    </h3>
                    <span className="text-xs text-foreground-500">{video.views}次播放</span>
                  </Link>
                ))}
              </div>
            </section>

            {/* Related Articles */}
            <section className="mt-10 pt-10 border-t border-background-200/70">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group block p-4 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors mb-1.5">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{ra.excerpt}</p>
                    <span className="text-xs text-foreground-400">{ra.date}</span>
                  </Link>
                ))}
              </div>
            </section>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}